import { BaseElement } from '../../../base-element';
import type { FeesFields, ValidationError } from '../../../../state/form.state';
import type { FeeBreakdown } from '../../../../services/fee-calculation';
import '../../../ui/dt-input';
import '../../../ui/dt-card';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export interface FeesSectionChangeDetail {
  section: 'fees';
  field: keyof FeesFields;
  path: string;
  value: string;
}

export class FeesSection extends BaseElement {
  private _fees: FeesFields | null = null;
  private _errors: ValidationError[] = [];
  private _feeBreakdown: FeeBreakdown | null = null;

  get fees(): FeesFields | null {
    return this._fees;
  }

  set fees(value: FeesFields | null) {
    this._fees = value;
    this.updateRender();
  }

  get errors(): ValidationError[] {
    return this._errors;
  }

  set errors(value: ValidationError[]) {
    this._errors = value ?? [];
    this.updateRender();
  }

  get feeBreakdown(): FeeBreakdown | null {
    return this._feeBreakdown;
  }

  set feeBreakdown(value: FeeBreakdown | null) {
    this._feeBreakdown = value;
    this.updateRender();
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private getError(path: string): string {
    return this._errors.find((error) => error.field === path)?.message ?? '';
  }

  private emitChange(field: keyof FeesFields, value: string): void {
    const path = `fees.${field}`;
    this.emit<FeesSectionChangeDetail>('section-change', {
      section: 'fees',
      field,
      path,
      value,
    });
  }

  private emitBlur(field: keyof FeesFields): void {
    const path = `fees.${field}`;
    this.emit('section-blur', { section: 'fees', field, path });
  }

  private attachEventListeners(): void {
    const input = this.$('dt-input[data-field="totalEntryFees"]');
    if (input) {
      input.addEventListener('input', (event: Event) => {
        const detail = (event as CustomEvent<{ value: string }>).detail;
        this.emitChange('totalEntryFees', detail?.value ?? '');
      });
      input.addEventListener('blur', () => this.emitBlur('totalEntryFees'));
    }
  }

  private formatFee(value: number): string {
    return `${new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)} USD`;
  }

  private getFormattedTotal(): string | null {
    const value = this._fees?.totalEntryFees ?? '';
    if (!value) return null;
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return null;
    return this.formatFee(numericValue);
  }

  private renderPricingTierBadge(): string {
    if (!this._feeBreakdown) return '';
    const tier = this._feeBreakdown.pricingTier;
    const colors: Record<string, string> = {
      PreEntry: 'bg-green-100 text-green-800',
      DayOf: 'bg-amber-100 text-amber-800',
      Closed: 'bg-red-100 text-red-800',
    };
    const colorClass = colors[tier] ?? 'bg-neutral-100 text-neutral-800';
    const labels: Record<string, string> = {
      PreEntry: 'Pre-Entry',
      DayOf: 'Day-of',
      Closed: 'Closed',
    };
    return `<span class="inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${colorClass}" data-testid="pricing-tier-badge">${labels[tier] ?? tier}</span>`;
  }

  private renderBreakdown(): string {
    if (!this._feeBreakdown || this._feeBreakdown.lineItems.length === 0) return '';

    const lineItemsHtml = this._feeBreakdown.lineItems.map((item) => `
      <div class="flex justify-between text-sm text-neutral-700">
        <span>${escapeHtml(item.stockType)} × ${item.trialCount} @ ${this.formatFee(item.unitPrice).replace(' USD', '')}</span>
        <span>${this.formatFee(item.lineTotal).replace(' USD', '')}</span>
      </div>
    `).join('');

    const discountsHtml = this._feeBreakdown.discounts.map((d) => `
      <div class="flex justify-between text-sm text-green-700">
        <span>${escapeHtml(d.description)}</span>
        <span>-${this.formatFee(d.amount).replace(' USD', '')}</span>
      </div>
    `).join('');

    return `
      <div class="flex flex-col gap-2 rounded-lg border border-neutral-100 bg-neutral-50 p-3" data-testid="fee-breakdown">
        ${lineItemsHtml}
        ${this._feeBreakdown.discounts.length > 0 ? `
          <div class="border-t border-neutral-200 pt-2">
            <div class="text-xs font-medium text-neutral-500 mb-1">Subtotal: ${this.formatFee(this._feeBreakdown.subtotal).replace(' USD', '')}</div>
            ${discountsHtml}
          </div>
        ` : ''}
      </div>
    `;
  }

  private updateRender(): void {
    const fees = this._fees;
    const formattedTotal = this.getFormattedTotal();

    this.render(`
      <section class="flex flex-col gap-3" data-testid="fees-section">
        <div class="flex items-center gap-2">
          <div class="text-sm font-semibold uppercase tracking-widest text-neutral-600">Entry Fees</div>
          ${this.renderPricingTierBadge()}
        </div>
        <dt-card variant="outlined">
          <div class="grid gap-4">
            ${this.renderBreakdown()}
            <div data-control-path="fees.totalEntryFees">
              <dt-input
                label="Total Entry Fees"
                value="${escapeHtml(fees?.totalEntryFees ?? '')}"
                required
                error="${escapeHtml(this.getError('fees.totalEntryFees'))}"
                data-field="totalEntryFees"
                data-path="fees.totalEntryFees"
                ${this._feeBreakdown ? 'readonly' : ''}
              ></dt-input>
            </div>
            ${formattedTotal ? `
              <div class="text-sm font-semibold text-neutral-700">Total: ${escapeHtml(formattedTotal)}</div>
            ` : ''}
            <div class="text-xs text-neutral-500">Payment details are not collected here.</div>
          </div>
        </dt-card>
      </section>
    `);

    this.attachEventListeners();
  }
}

customElements.define('dt-fees-section', FeesSection);
